/**
 * Menu template theme packages , curated color + font presets that can be
 * applied to any template (legacy-scroll or paged-flip) and flow through to
 * the public ordering page.
 *
 * Distinct from lib/theme/* (dashboard chrome themes). Theme data lives in
 * themes.json so scripts/validate-menu-themes.mjs can contrast-check it
 * without a TS toolchain; edit the JSON, then run the validator.
 */

import { colord } from 'colord';

import { blackOrWhiteForeground, colorReadableWithForeground, contrastRatio, readableColorOnSurfaces } from './contrast';
import themesData from './themes.json';

export type MenuThemeMode = 'light' | 'dark';

/**
 * Optional CSS gradient layers a palette can ship on top of its solid colors.
 * Every gradient always has a solid fallback (the matching color key), so
 * consumers that only understand `backgroundColor` keep working unchanged.
 */
export interface MenuThemeGradients {
  /** Canvas/page background , rendered where the CSS `background` shorthand is used. */
  background?: string;
  /** Promo strip/badge background. */
  promo?: string;
}

/** Matches CANONICAL_COLOR_KEYS in lib/mnrq/template-variable-standards.ts. */
export interface MenuThemeColors {
  background: string;
  surface: string;
  primary: string;
  secondary: string;
  text: string;
  muted: string;
  accent: string;
  divider: string;
  promoBg: string;
  promoText: string;
  promoBorder: string;
  availabilityBg: string;
  availabilityText: string;
  hero: string;
  gradients?: MenuThemeGradients;
}

/** Font role values are names from the lib/types.ts `fonts` registry. */
export interface MenuThemeFonts {
  heading: string;
  body: string;
  display?: string;
  logo?: string;
  price?: string;
}

export interface MenuThemePackage {
  id: string;
  name: string;
  description: string;
  tags: string[];
  fonts: MenuThemeFonts;
  modes: Partial<Record<MenuThemeMode, MenuThemeColors>>;
  defaultMode: MenuThemeMode;
  swatch: { background: string; surface: string; text: string; accent: string };
}

export const MENU_THEMES: readonly MenuThemePackage[] = themesData.themes as MenuThemePackage[];

const THEMES_BY_ID = new Map(MENU_THEMES.map((theme) => [theme.id, theme]));

export function getMenuTheme(id: string | null | undefined): MenuThemePackage | null {
  if (!id) return null;
  return THEMES_BY_ID.get(id) || null;
}

/** Every registered theme resolves both modes, deriving a missing counterpart. */
export function isDualModeMenuTheme(theme: MenuThemePackage): boolean {
  return Boolean(theme.modes.light || theme.modes.dark);
}

/**
 * Resolves an authored palette, or builds an accessible opposite-mode variant
 * when a theme only defines light or dark. This keeps older one-mode themes
 * usable with the guest's appearance preference without pinning the page.
 */
export function resolveMenuThemeColors(
  theme: MenuThemePackage,
  mode?: MenuThemeMode,
): { mode: MenuThemeMode; colors: MenuThemeColors } {
  const requested = mode || theme.defaultMode;
  const authored = theme.modes[requested];
  if (authored) return { mode: requested, colors: ensureReadablePalette(authored) };

  const source = theme.modes[theme.defaultMode] || theme.modes.light || theme.modes.dark;
  if (!source) {
    throw new Error(`Menu theme "${theme.id}" has no palette for its defaultMode`);
  }
  return { mode: requested, colors: deriveOppositeModeColors(source, requested) };
}

function hslTone(color: string, mode: MenuThemeMode, lightness: number): string {
  const parsed = colord(color);
  if (!parsed.isValid()) return mode === 'dark' ? '#111312' : '#f8f8f5';
  const { h, s } = parsed.toHsl();
  return colord({ h, s: Math.min(s, 32), l: lightness }).toHex();
}

function ensureReadablePalette(source: MenuThemeColors): MenuThemeColors {
  const surfaces = [source.background, source.surface];
  const mutedSurfaces = [...surfaces, source.secondary];
  const readableText = contrastRatio(source.text, source.background) >= 4.5 && contrastRatio(source.text, source.surface) >= 4.5
    ? source.text
    : readableColorOnSurfaces(source.text, surfaces, 4.5);
  const fixText = (color: string, backgrounds: string[]) =>
    backgrounds.every((background) => contrastRatio(color, background) >= 4.5)
      ? color
      : readableColorOnSurfaces(color, backgrounds, 4.5);

  return {
    ...source,
    text: readableText,
    primary: colorReadableWithForeground(source.primary, readableText),
    // `secondary` is the tinted surface role (for example the pale blue rows
    // in Ocean Slate), not a text role. Mutating it to pass text contrast made
    // downstream buttons use a mid-blue fill with dark body text and made soft
    // labels disappear. `muted` is the paired text role and is checked below.
    secondary: source.secondary,
    muted: fixText(source.muted, mutedSurfaces),
    accent: colorReadableWithForeground(source.accent, readableText),
    promoText: fixText(source.promoText, [source.promoBg]),
    availabilityText: fixText(source.availabilityText, [source.availabilityBg]),
  };
}

function deriveOppositeModeColors(source: MenuThemeColors, mode: MenuThemeMode): MenuThemeColors {
  const isDark = mode === 'dark';
  const background = hslTone(source.background, mode, isDark ? 8 : 97);
  const surface = hslTone(source.surface, mode, isDark ? 14 : 93);
  const secondary = hslTone(source.secondary, mode, isDark ? 20 : 92);
  const promoBg = hslTone(source.promoBg, mode, isDark ? 20 : 95);
  const availabilityBg = hslTone(source.availabilityBg, mode, isDark ? 18 : 94);
  const text = blackOrWhiteForeground(background);
  const textSurfaces = [background, surface, secondary];

  return {
    background,
    surface,
    primary: colorReadableWithForeground(source.primary, text),
    // The secondary token remains a surface tint in both modes. Keep its hue
    // and make a subdued surface for the requested appearance; contrast is
    // handled by `muted` / `text`, which are the actual foreground roles.
    secondary,
    text,
    muted: readableColorOnSurfaces(source.muted, textSurfaces, 4.5),
    accent: colorReadableWithForeground(source.accent, text),
    divider: hslTone(source.divider, mode, isDark ? 28 : 83),
    promoBg,
    promoText: blackOrWhiteForeground(promoBg),
    promoBorder: hslTone(source.promoBorder, mode, isDark ? 35 : 78),
    availabilityBg,
    availabilityText: blackOrWhiteForeground(availabilityBg),
    hero: hslTone(source.hero, mode, isDark ? 12 : 90),
  };
}
